import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Modal } from '@/components/ui/overlays';
import { Button } from '@/components/ui/primitives';

/**
 * Ends an idle session, with warning.
 *
 * The server is the authority: every authenticated request slides a session
 * row forward, and a request made after the window closes gets a 401 whatever
 * the browser believes. This is the courtesy layer on top — it warns before
 * the window shuts, and clears up promptly afterwards so a shared warehouse
 * tablet is not left showing customer data to whoever walks past.
 *
 * The countdown restarts on genuine interaction, not on a timer tick: the
 * browser and the server therefore agree on what "idle" means, because the
 * same interactions are what produce the API calls that slide the session.
 */

/** How long before the deadline the warning appears. */
const WARN_BEFORE_MS = 5 * 60_000;

/** Interactions that count as being at the desk. */
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;

export function InactivityGuard() {
  const { isAuthenticated, session, signOut, refresh } = useAuth();
  const idleMs = (session?.idleMinutes ?? 30) * 60_000;

  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  /** Wall-clock deadline; compared against Date.now() rather than counted down. */
  const deadline = useRef(Date.now() + idleMs);
  const warned = useRef(false);

  const resetWindow = useCallback(() => {
    deadline.current = Date.now() + idleMs;
    warned.current = false;
    setWarningOpen(false);
  }, [idleMs]);

  // Any real interaction pushes the deadline out. Throttled to once a second:
  // a scroll fires this hundreds of times and none of them need a re-render.
  useEffect(() => {
    if (!isAuthenticated) return;
    let last = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - last < 1000) return;
      last = now;
      // While the warning is up, only the button dismisses it — an accidental
      // scroll should not silently extend a session the user was told about.
      if (warned.current) return;
      deadline.current = now + idleMs;
    };
    for (const e of ACTIVITY_EVENTS) window.addEventListener(e, onActivity, { passive: true });
    return () => {
      for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, onActivity);
    };
  }, [isAuthenticated, idleMs]);

  // Re-anchor whenever the server tells us where the window now closes.
  useEffect(() => {
    if (session?.expiresAt) {
      deadline.current = new Date(session.expiresAt).getTime();
      warned.current = false;
      setWarningOpen(false);
    }
  }, [session?.expiresAt]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const tick = setInterval(() => {
      const remaining = deadline.current - Date.now();

      if (remaining <= 0) {
        clearInterval(tick);
        // `reason` is read by the sign-in page to explain what happened,
        // rather than dumping the user at a login form with no explanation.
        void signOut('inactivity');
        return;
      }

      if (remaining <= WARN_BEFORE_MS) {
        warned.current = true;
        setWarningOpen(true);
        setSecondsLeft(Math.ceil(remaining / 1000));
      }
    }, 1000);

    return () => clearInterval(tick);
  }, [isAuthenticated, signOut]);

  const stayLoggedIn = async () => {
    // Any authenticated call slides the server's window; `me` also returns the
    // new expiry, so the client re-anchors instead of assuming.
    await api.get('/api/auth/me').catch(() => undefined);
    await refresh();
    resetWindow();
  };

  if (!isAuthenticated) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = String(secondsLeft % 60).padStart(2, '0');

  return (
    <Modal
      isOpen={warningOpen}
      onClose={stayLoggedIn}
      title="Your session is about to expire"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => void signOut('manual')}>Sign out now</Button>
          <Button variant="primary" onClick={() => void stayLoggedIn()}>Stay logged in</Button>
        </>
      }
    >
      <p className="text-body text-ink-secondary">
        You will be signed out in{' '}
        <span className="tabular font-medium text-ink">
          {minutes}:{seconds}
        </span>{' '}
        because of inactivity. Anything you have typed but not saved will be lost.
      </p>
    </Modal>
  );
}
